import PropTypes from 'prop-types';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FormDrawer } from 'common/components/drawer';
import { FormBody } from 'common/components/drawer';
import { FormFooter } from 'common/components/drawer';
import { useForm } from 'utils/hooks';
import useRouter from 'use-react-router';

import { Button } from 'reactstrap';

import { DrawerState } from 'common/entities/drawer/DrawerTypes';
import sectionConfig from './config';

import {
  createPurchasingStore,
  editPurchasingStore,
  importCompaniesToPurchasingStore,
  linkPurchasingStore
} from 'store/purchasing-stores/actions';

const DrawerBody = ({ type, drawer, active, refetchData }) => {
  const [isSaving, setIsSaving] = useState();
  const dispatch = useDispatch();
  const config = useMemo(() => sectionConfig[type]?.formConfig, [type]);
  const { match } = useRouter();
  const { params } = match;
  const { organizationId: urlOrganizationId } = params;

  const { formState, collectValues, loadValues, resetForm } = useForm(config, {});

  const closeForm = useCallback(() => {
    drawer.close();
  }, [drawer]);

  useEffect(() => {
    if (drawer.isOpen) {
      resetForm();
    }
  }, [drawer.isOpen, resetForm]);

  useEffect(() => {
    if (active && type !== 'create') {
      if (type === 'edit') {
        loadValues(active);
      } else {
        loadValues({ id: active?.id });
      }
    }
  }, [active, drawer.isOpen, loadValues, resetForm, type]);

  const onSubmit = async () => {
    const values = collectValues();

    if (!values) return;
    setIsSaving(true);

    try {
      if (type === 'create') {
        const { store_group, photo, ...rest } = values;
        const parseValues = {
          ...rest,
          store_group_id: store_group?.id || null,
          photo_id: photo?.[0].id || null
        };
        await dispatch(createPurchasingStore(parseValues)).unwrap();
      }

      if (type === 'edit') {
        const { store_group, photo, ...rest } = values;

        const parseValues = {
          ...rest,
          store_group_id: store_group?.id || null,
          photo_id: photo?.[0].id || null
        };

        await dispatch(editPurchasingStore(parseValues)).unwrap();
      }

      if (type === 'import') {
        const { organization, store_group, ...rest } = values;
        const parseValues = {
          ...rest,
          organization_id: organization?.id || null,
          store_group_id: store_group?.id || null
        };

        await dispatch(importCompaniesToPurchasingStore(parseValues)).unwrap();
      }

      if (type === 'link') {
        // We link an ocean item with a core item
        // So in url we use the core item id
        // In params the ocean item id
        // The selector options are the core purchasing items and the core purchasing groups

        const { store_group, equivalent_item, ...rest } = values;

        const parseValues = {
          ...rest,
          organization_store_id: equivalent_item?.id || null
        };

        await dispatch(linkPurchasingStore(parseValues)).unwrap();
      }

      if (type === 'ocean-link') {
        // We link a core item with an ocean item
        // So in url we use the ocean item id
        // In params the core item id
        // The selector options are the ocean purchasing items and the ocean purchasing groups

        const { store_group, id, equivalent_item, ...rest } = values;

        if (!urlOrganizationId) return;

        const parseValues = {
          ...rest,
          id: equivalent_item?.id,
          organization_id: urlOrganizationId,
          organization_store_id: id
        };

        await dispatch(linkPurchasingStore(parseValues)).unwrap();
      }

      refetchData();
      setIsSaving(false);
      closeForm();
    } catch (err) {
      setIsSaving(false);
    }
  };

  const Section = sectionConfig[type]?.component;

  return (
    <>
      <FormDrawer>
        <FormBody>
          <Section formState={formState} drawer={drawer} id={active?.id} />
        </FormBody>
      </FormDrawer>

      <FormFooter className="p-3 d-flex justify-content-end">
        <Button color="cancel" className="px-0 py-1 me-4" onClick={closeForm} disabled={isSaving}>
          {type === 'equivalent' ? 'CLOSE' : 'CANCEL'}
        </Button>
        {type === 'equivalent' ? null : (
          <Button
            onClick={onSubmit}
            disabled={isSaving}
            color="primary"
            className="px-4 text-uppercase"
          >
            {type === 'edit' ? 'SAVE' : type === 'ocean-link' ? 'LINK' : type}
          </Button>
        )}
      </FormFooter>
    </>
  );
};

export default DrawerBody;

DrawerBody.propTypes = {
  type: PropTypes.string.isRequired,
  drawer: DrawerState.isRequired,
  active: PropTypes.shape({
    id: PropTypes.number,
    parent_id: PropTypes.number,
    name: PropTypes.string,
    code: PropTypes.string
  }),
  refetchData: PropTypes.func
};
